import React from 'react';
import { Row, Col, Flex, Typography, theme } from 'antd';
import { useLocation, useNavigationType } from 'react-router-dom';

import useQueryStringParameters from '../../utils/useQueryStringParameters';
import {
  ALL_CRAWLERS,
  type CrawlerFilterSearchParams,
  fromSearchParams,
  toCategories,
  toSearchParams,
  toStored,
} from '~features/crawler-filter';
import { useCrawlerCategoryScheme, useCrawlerSelection } from '~features/crawler-filter/CrawlerFilterHooks';
import { CRAWLERS_LIST_ENDPOINT, useGetCrawlersFiltersListQuery } from '~features/crawler-filter/CrawlerFilterApi';

import { usePrerenderUserId, useCurrentPlan, useHasTrackingCode } from '../../hooks/usePrerenderUser';
import KPIPagesCrawled from './KPIPagesCrawled';
import KPICacheHitsRatio from './KPICacheHitsRatio';
import KPIErrorRate from './KPIErrorRate';
import KPIAverageCrawlTime from './KPIAverageCrawlTime';
import ChartPagesCrawled, { PAGES_CRAWLED_VISIBILITY_CHARTS } from './ChartPagesCrawled';
import ChartStatusCodes, { STATUS_CODES_VISIBILITY_CHARTS } from './ChartStatusCodes';
import ChartAverageCrawlTime, { AVERAGE_CRAWL_TIME_VISIBILITY_CHARTS } from './ChartAverageCrawlTime';
import ChartCrawlerType, { CRAWLER_TYPE_VISIBILITY_CHARTS } from './ChartCrawlerType';
import ChartPagesRendered, { PAGES_RENDERED_VISIBILITY_CHARTS } from './ChartPagesRendered';
import Filters, { getDefaultDateRange, type FiltersUpdate } from '~components/Filters';
import { PrintableExport } from '~components/PrintableExport';
import { useChartDescriptionsState } from '~components/Charts';
import Greeting from './components/Greeting';
import { CrawlerTrafficEmptyState, EmptyRangeState, NoCrawlersSelected, queryErrorDetail } from '~features/empty-states';
import { useCrawlerTrafficEmptyScope, widenedDateWindow } from '~features/empty-states/scope';
import {
  PAGES_CRAWLED_ENDPOINT,
  useGetCacheHitsRatioSummaryQuery,
  useGetPagesCrawledQuery,
  useGetPagesCrawledSummaryQuery,
  useGetStatusCodesQuery,
  useGetErrorRateSummaryQuery,
  useGetAverageCrawlTimeSummaryQuery,
  useGetAverageCrawlTimeQuery,
  useGetPagesRenderedQuery,
} from './DashboardV3API';
import { buildDashboardV3QueryParameters, type DashboardV3QueryStrings } from './QueryParameters';
import * as Storage from './Storage';
import useTrackDashboardV3Event from './useTrackDashboardV3Event';
import { appendHiddenChartItems } from '~library/chartjs';
import { IsPrintingProvider } from '~library/print';
import { computeDashboardV3Filters, loadSavedFilters } from './dashboardV3Filters';
import PrintableDashboard from './PrintableDashboard';
import { CrawlerPickerAnnouncement } from './crawler-picker-announcement';
import { PaidUserSurveyTrigger } from '../CustomerSupport';
import { RenderProofEntryPoint } from '../render-proof';


const PRINTABLE_CHARTS = [
  ...PAGES_CRAWLED_VISIBILITY_CHARTS,
  ...CRAWLER_TYPE_VISIBILITY_CHARTS,
  ...STATUS_CODES_VISIBILITY_CHARTS,
  ...AVERAGE_CRAWL_TIME_VISIBILITY_CHARTS,
  ...PAGES_RENDERED_VISIBILITY_CHARTS,
];

const PRINTABLE_CHART_IDS = PRINTABLE_CHARTS.map((chart) => chart.id);

type WidgetResult<RawData> = { data?: RawData; isFetching: boolean; isError: boolean; refetch: () => unknown };

function DashboardV3Page({ print }: { print?: boolean }) {
  const { search: locationSearch } = useLocation();
  const navigationType = useNavigationType();
  const { dateFrom: DEFAULT_DATE_FROM, dateTo: DEFAULT_DATE_TO } = getDefaultDateRange();
  const isPrinting = Boolean(print);
  const [chartDescriptionsEnabled, onChartDescriptionsEnabledChange] = useChartDescriptionsState();

  const pageviewSent = React.useRef(false);
  const pageEntered = React.useRef(false);
  const track = useTrackDashboardV3Event();
  const { token } = theme.useToken();
  const userId = usePrerenderUserId();
  const notIntegrated = !useHasTrackingCode();
  const [storedValuesReady, setStoredValuesReady] = React.useState(false);
  const [includeScheduledRenders, setIncludeScheduledRenders] = React.useState(true);
  const currentPlan = useCurrentPlan();
  const categoryScheme = useCrawlerCategoryScheme();
  const { queryStrings, updateMultipleParameters } = useQueryStringParameters<
    DashboardV3QueryStrings,
    DashboardV3QueryStrings & Partial<CrawlerFilterSearchParams>
  >(buildDashboardV3QueryParameters);
  const crawlerFilter = React.useMemo(
    () => fromSearchParams(new URLSearchParams(locationSearch), categoryScheme),
    [locationSearch, categoryScheme]
  );

  // MULTIPLE REQUESTS FOR EXACT ERROR MESSAGES
  const AllCrawlersResult = useGetCrawlersFiltersListQuery({ categoryScheme }, { skip: notIntegrated });
  const crawlersListReady = AllCrawlersResult.isSuccess;
  const { crawlerUniverse, effectiveCrawlerFilter, pageCrawlerFilter, noCrawlersInScope, noteFiltersUpdate } =
    useCrawlerSelection({ urlFilter: crawlerFilter, everCrawled: AllCrawlersResult, categoryScheme, isPrinting });
  const selectedCrawlerCategories = React.useMemo(
    () => toCategories(effectiveCrawlerFilter, categoryScheme),
    [effectiveCrawlerFilter, categoryScheme]
  );

  const filtersValue = {
    dateFrom: queryStrings.dateFrom || DEFAULT_DATE_FROM,
    dateTo: queryStrings.dateTo || DEFAULT_DATE_TO,
    crawlers: selectedCrawlerCategories,
    domain: queryStrings.domain,
    categoryScheme,
  };

  const skipQuery = notIntegrated || !crawlersListReady || !storedValuesReady || noCrawlersInScope;

  // CHARTS RELATED API REQUESTS
  const queryOptions = {
    skip: skipQuery,
  };
  const pagesRenderedFiltersValue = {
    ...filtersValue,
    includeScheduledRenders,
  };
  const PagesCrawledResult = useGetPagesCrawledQuery(filtersValue, queryOptions);
  const PagesCrawledSummaryResult = useGetPagesCrawledSummaryQuery(filtersValue, queryOptions);
  const StatusCodesResult = useGetStatusCodesQuery(filtersValue, queryOptions);
  const CacheHitRatioSummaryResult = useGetCacheHitsRatioSummaryQuery(filtersValue, queryOptions);
  const ErrorRateSummaryResult = useGetErrorRateSummaryQuery(filtersValue, queryOptions);
  const AverageCrawlTimeSummaryResult = useGetAverageCrawlTimeSummaryQuery(filtersValue, queryOptions);
  const AverageCrawlTimeResult = useGetAverageCrawlTimeQuery(filtersValue, queryOptions);
  const PagesRenderedResult = useGetPagesRenderedQuery(pagesRenderedFiltersValue, queryOptions);

  const metricResults = [
    PagesCrawledResult,
    PagesCrawledSummaryResult,
    StatusCodesResult,
    CacheHitRatioSummaryResult,
    ErrorRateSummaryResult,
    AverageCrawlTimeSummaryResult,
    AverageCrawlTimeResult,
    PagesRenderedResult,
  ];
  const isEverythingLoaded = !skipQuery && metricResults.every((result) => !result.isFetching);

  const pageLoadError = AllCrawlersResult.isError
    ? { detail: queryErrorDetail(AllCrawlersResult.error, CRAWLERS_LIST_ENDPOINT), retry: AllCrawlersResult.refetch }
    : metricResults.every((result) => result.isError)
      ? {
          detail: queryErrorDetail(PagesCrawledResult.error, PAGES_CRAWLED_ENDPOINT),
          retry: () => metricResults.forEach((result) => result.refetch()),
        }
      : undefined;

  const updateParametersAndPersist = (
    { crawlerFilter: newCrawlerFilter, ...newFilters }: FiltersUpdate,
    { persistCrawlers = true }: { persistCrawlers?: boolean } = {}
  ) => {
    noteFiltersUpdate({ crawlerFilter: newCrawlerFilter });
    const crawlerParams = newCrawlerFilter ? toSearchParams(newCrawlerFilter) : {};
    const storedCrawlers = newCrawlerFilter && persistCrawlers ? toStored(newCrawlerFilter) : {};
    updateMultipleParameters({ ...newFilters, ...crawlerParams }, { resetPagination: false, filterNullish: true });
    Storage.saveFilters({ ...newFilters, ...storedCrawlers }, userId);
  };

  const hasDataForTimeRange = !!PagesCrawledResult.data?.data.length;

  /** PDF includes all charts — align export UX with `isEverythingLoaded` + pages-crawled “has rows” (empty state). */
  const canExportDashboardPdf = isEverythingLoaded && hasDataForTimeRange && !pageLoadError;
  const exportDashboardLoading = !isEverythingLoaded && !noCrawlersInScope && !pageLoadError;

  const noDataInRange = !PagesCrawledResult.isFetching && !PagesCrawledResult.isError && !hasDataForTimeRange && !skipQuery;
  const emptyScope = useCrawlerTrafficEmptyScope({
    emptyInRange: noDataInRange,
    crawlerList: AllCrawlersResult,
    allDomains: !filtersValue.domain,
    crawlerFilter: effectiveCrawlerFilter,
    selectedCrawlerCategories,
    dateFrom: filtersValue.dateFrom,
    dateTo: filtersValue.dateTo,
    isPrinting,
  });
  const widgetsLoading = skipQuery || emptyScope.counting;
  const showEmptyRangeState = noDataInRange && !emptyScope.counting;
  const widget = <RawData,>(result: { data?: RawData; isFetching: boolean }) => ({
    rawData: result.data,
    isLoading: result.isFetching || widgetsLoading,
  });
  const liveWidget = <RawData,>(result: WidgetResult<RawData>) => ({
    ...widget(result),
    error: result.isError ? { onRetry: () => result.refetch() } : undefined,
  });
  const clearFilters = () =>
    updateParametersAndPersist({
      domain: undefined,
      crawlerFilter: ALL_CRAWLERS,
      dateFrom: DEFAULT_DATE_FROM,
      dateTo: DEFAULT_DATE_TO,
    });
  const widenDateRange = () => updateParametersAndPersist(widenedDateWindow());
  const emptyRangeState = showEmptyRangeState ? (
    <EmptyRangeState
      screen="dashboard"
      scope={emptyScope.state}
      onClearFilters={clearFilters}
      onWidenDateRange={widenDateRange}
    />
  ) : undefined;

  const dateRange: [Date, Date] = React.useMemo(
    () => [filtersValue.dateFrom, filtersValue.dateTo],
    [filtersValue.dateFrom, filtersValue.dateTo]
  );

  React.useEffect(() => {
    const savedFilters = loadSavedFilters(userId);
    // React Router reports the initial load as POP, the same as Back/Forward.
    const enteringPage = !pageEntered.current || navigationType !== 'POP';
    pageEntered.current = true;

    const { paramsToOverride, analyticsData } = computeDashboardV3Filters({
      queryStrings,
      crawlerFilter,
      savedFilters,
      restoreSavedDomain: enteringPage && !new URLSearchParams(locationSearch).has('domain'),
      dataRetentionDays: currentPlan.dataRetentionDays,
      allCrawlersList: crawlerUniverse,
      defaultDateFrom: DEFAULT_DATE_FROM,
      defaultDateTo: DEFAULT_DATE_TO,
      categoryScheme,
    });

    setIncludeScheduledRenders(savedFilters?.includeScheduledRenders ?? true);
    if (Object.keys(paramsToOverride).length > 0) {
      updateParametersAndPersist(paramsToOverride, { persistCrawlers: false });
    }
    setStoredValuesReady(true);

    // COMPUTE DATA FOR MIXPANEL
    if (!pageviewSent.current && crawlersListReady && !isPrinting) {
      track('Viewed', analyticsData);
      pageviewSent.current = true;
    }
  }, [
    crawlerUniverse,
    crawlerFilter,
    categoryScheme,
    queryStrings.dateFrom,
    queryStrings.dateTo,
    currentPlan.dataRetentionDays,
    isPrinting,
  ]);

  const printReady = isEverythingLoaded || notIntegrated || Boolean(pageLoadError);

  if (!isPrinting) {
    return (
      <IsPrintingProvider initialValue={false}>
        {!notIntegrated && <CrawlerPickerAnnouncement />}
        <RenderProofEntryPoint />
        <PaidUserSurveyTrigger />

        <Greeting />
        {!notIntegrated && (
          <Filters
            trackEvent={track}
            updateFilters={updateParametersAndPersist}
            domainSelected={filtersValue.domain}
            crawlerFilter={pageCrawlerFilter}
            dateFrom={filtersValue.dateFrom}
            dateTo={filtersValue.dateTo}
            export={{
              label: 'Export as PDF',
              url: '/export-dashboard',
              search: () => appendHiddenChartItems(locationSearch, PRINTABLE_CHARTS),
              disabled: !canExportDashboardPdf,
              loading: exportDashboardLoading,
            }}
            chartDescriptions={{
              enabled: chartDescriptionsEnabled,
              onChange: onChartDescriptionsEnabledChange,
            }}
          />
        )}

        {notIntegrated ? (
          <CrawlerTrafficEmptyState screen="dashboard" kind="not-integrated" />
        ) : noCrawlersInScope ? (
          <NoCrawlersSelected
            screen="dashboard"
            onSelectAllCrawlers={() => updateParametersAndPersist({ crawlerFilter: ALL_CRAWLERS })}
          />
        ) : pageLoadError ? (
          <CrawlerTrafficEmptyState
            screen="dashboard"
            kind="error"
            detail={pageLoadError.detail}
            onRetry={pageLoadError.retry}
          />
        ) : emptyRangeState ? (
          emptyRangeState
        ) : (
          <>
            <div style={{ marginBottom: token.marginXL, marginTop: token.marginXL }}>
              <Typography.Title level={4} style={{ color: token.colorTextTertiary, marginBottom: token.marginMD }}>
                KPIs
              </Typography.Title>
              <Flex gap="middle" style={{ overflowX: 'auto' }}>
                <KPIPagesCrawled {...liveWidget(PagesCrawledSummaryResult)} />
                <KPICacheHitsRatio {...liveWidget(CacheHitRatioSummaryResult)} />
                <KPIErrorRate {...liveWidget(ErrorRateSummaryResult)} />
                <KPIAverageCrawlTime {...liveWidget(AverageCrawlTimeSummaryResult)} />
              </Flex>
            </div>
            <div>
              <Typography.Title level={4} style={{ color: token.colorTextTertiary, marginBottom: token.marginMD }}>
                Crawler Activity
              </Typography.Title>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <ChartPagesCrawled
                    dateRange={dateRange}
                    {...liveWidget(PagesCrawledResult)}
                    descriptionVisible={chartDescriptionsEnabled}
                  />
                </Col>
                <Col span={24}>
                  <ChartCrawlerType
                    dateRange={dateRange}
                    {...liveWidget(PagesCrawledResult)}
                    descriptionVisible={chartDescriptionsEnabled}
                  />
                </Col>
                <Col span={24} xl={12}>
                  <ChartAverageCrawlTime
                    dateRange={dateRange}
                    {...liveWidget(AverageCrawlTimeResult)}
                    descriptionVisible={chartDescriptionsEnabled}
                  />
                </Col>
                <Col span={24} xl={12}>
                  <ChartStatusCodes
                    dateRange={dateRange}
                    {...liveWidget(StatusCodesResult)}
                    descriptionVisible={chartDescriptionsEnabled}
                  />
                </Col>
              </Row>
              <Typography.Title
                level={4}
                style={{ color: token.colorTextTertiary, marginBottom: token.marginMD, marginTop: token.marginXL }}
              >
                Performance & Health
              </Typography.Title>
              <Row gutter={[16, 16]}>
                <Col span={24} xl={24}>
                  <ChartPagesRendered
                    dateRange={dateRange}
                    {...liveWidget(PagesRenderedResult)}
                    descriptionVisible={chartDescriptionsEnabled}
                    includeScheduledRenders={includeScheduledRenders}
                    onIncludeScheduledRendersChange={(value) => {
                      setIncludeScheduledRenders(value);
                      Storage.saveFilters({ includeScheduledRenders: value }, userId);
                    }}
                  />
                </Col>
              </Row>
            </div>
          </>
        )}
      </IsPrintingProvider>
    );
  }

  return (
    <PrintableExport subject="dashboard" ready={printReady} chartIds={PRINTABLE_CHART_IDS}>
      <PrintableDashboard
        filters={{
          ...filtersValue,
          dateRange,
        }}
        emptyState={
          notIntegrated ? (
            <CrawlerTrafficEmptyState screen="dashboard" kind="not-integrated" />
          ) : pageLoadError ? (
            <CrawlerTrafficEmptyState
              screen="dashboard"
              kind="error"
              detail={pageLoadError.detail}
              onRetry={pageLoadError.retry}
            />
          ) : (
            emptyRangeState
          )
        }
        kpis={{
          pagesCrawled: widget(PagesCrawledSummaryResult),
          cacheHitsRatio: widget(CacheHitRatioSummaryResult),
          errorRate: widget(ErrorRateSummaryResult),
          averageCrawlTime: widget(AverageCrawlTimeSummaryResult),
        }}
        charts={{
          pagesCrawled: widget(PagesCrawledResult),
          crawlerType: widget(PagesCrawledResult),
          averageCrawlTime: widget(AverageCrawlTimeResult),
          statusCodes: widget(StatusCodesResult),
          pagesRendered: { ...widget(PagesRenderedResult), includeScheduledRenders },
        }}
      />
    </PrintableExport>
  );
}

export default DashboardV3Page;
